// Mirror of the unblocker's per-proxy serving gate (app/proxy.py::_proxy_serves): a proxy
// serves a host iff its effective capabilities (stored ∪ attribute-derived) satisfy the host's
// required capabilities, its only_sites reserve it (when set), and no deny_sites exclude it.
// Shared by the Proxy Management (Pool / Site Rules) and Canary screens so their per-host
// eligibility counts always agree with actual routing.

// Attribute-derived capabilities, auto-available on every proxy without manual tagging:
// type:<type>, source:<source>, geo:<country>.
export const derivedCaps = (p) => [
    p.type && ('type:' + p.type),
    p.source && ('source:' + p.source),
    p.country && ('geo:' + String(p.country).toLowerCase()),
].filter(Boolean);

// Suffix match: a rule/scope host "n11.com" also covers "urun.n11.com".
export const hostMatch = (host, site) => {
    const h = (host || '').toLowerCase();
    const s = (site || '').toLowerCase();
    return !!s && (h === s || h.endsWith('.' + s));
};

// A rule's `requires` is a flat list of caps (ONE group) OR a list of such groups that are OR'd
// together, e.g. [[source:iproyal, type:datacenter], [hb-ok]] = (iproyal AND datacenter) OR hb-ok.
// Normalize to a list of groups (empty groups dropped; empty requires -> []).
export const asGroups = (requires) => {
    if (!requires || !requires.length) return [];
    return Array.isArray(requires[0]) ? requires.filter(g => g && g.length) : [requires];
};

// Every cap across all groups, flattened (for flat scans like capability suggestions).
export const flatCaps = (requires) => asGroups(requires).flat();

// One group holds when the proxy covers every capability KIND it names; caps of the SAME kind
// (geo:/source:/type:, or a prefixless custom cap) are OR'd, different kinds AND'd. Mirrors the
// unblocker's _requires_satisfied.
export const groupSatisfied = (group, eff) => {
    const kinds = {};
    (group || []).forEach(c => {
        const kind = c.split(':')[0];
        (kinds[kind] = kinds[kind] || []).push(c);
    });
    return Object.values(kinds).every(opts => opts.some(c => eff.has(c)));
};

// The whole rule holds when ANY OR-group is satisfied. No requires -> matches everything.
export const ruleSatisfied = (requires, eff) => {
    const groups = asGroups(requires);
    return groups.length === 0 || groups.some(g => groupSatisfied(g, eff));
};

// Capabilities a host requires, from site rules ([{host, requires}]); the longest-matching
// rule host wins. [] when no rule matches (the site is open to every eligible proxy).
export const requiresFor = (host, siteRules) => {
    let best = null;
    (siteRules || []).forEach(r => {
        const rh = (r.host || '').toLowerCase();
        if (hostMatch(host, rh) && (!best || rh.length > (best.host || '').length)) best = r;
    });
    return (best && best.requires) || [];
};

// Does proxy `p` serve `host` given its explicit `requires`? capability + reservation + deny.
export const proxyServes = (p, host, requires) => {
    const eff = new Set([...(p.capabilities || []), ...derivedCaps(p)]);
    if (!ruleSatisfied(requires, eff)) return false;
    const only = p.only_sites || p.allow_sites || [];  // only_sites (new) / allow_sites (legacy)
    if (only.length && !only.some(s => hostMatch(host, s))) return false;
    if ((p.deny_sites || []).some(s => hostMatch(host, s))) return false;
    return true;
};

// Convenience: resolve the host's requires from siteRules, then check serving.
export const serves = (p, host, siteRules) => proxyServes(p, host, requiresFor(host, siteRules));
